<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onMounted, onUnmounted, provide, ref, shallowRef, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import {
  PlayerAccessRequiredError,
  PlayerSessionUnauthorizedError,
  playerApi,
  type PlayerMatchmakerQueue,
  type PlayerMatchmakerState,
  type PlayerParty,
  type PlayerQueueBan,
  type PlayerMeResponse,
} from '@/api/public'
import CopyCommandButton from '@/components/CopyCommandButton.vue'
import MatchSearchPanel from '@/components/MatchSearchPanel.vue'
import PlayerApprovalNotice from '@/components/player/PlayerApprovalNotice.vue'
import PlayerDock from '@/components/player/PlayerDock.vue'
import PlayerQueueLanes from '@/components/player/PlayerQueueLanes.vue'
import PlayerSoundSettings from '@/components/player/PlayerSoundSettings.vue'
import { startPublicStatusPolling, stopPublicStatusPolling } from '@/lib/publicFreshness'
import {
  publicLiveCountState,
  startPublicLiveCount,
  stopPublicLiveCount,
} from '@/lib/publicLiveCount'
import {
  canCancelPartyMatchmaking,
  canStartPartyMatchmaking,
  focusTrapDestination,
  queueAllowsPartySize,
} from '@/lib/playerSocialPresentation'
import {
  formatQueueSearchElapsed,
  queueSearchElapsedSeconds,
  reconcileQueueSearchTimer,
  type QueueSearchTimerAnchor,
} from '@/lib/queueSearchTimer'
import {
  playerProfileSocialActionsKey,
  type PlayerProfileSocialActionsController,
} from '@/lib/playerProfileSocialActions'
import { backdropSupported, whenIdle } from '@/lib/dashboardBackdrop/support'
import { playerHistoricalAccessAllowedKey } from '@/lib/playerHistoricalAccess'
import { playerModeratorKey } from '@/lib/playerModerator'
import { resolvePlayerPartyRosterLock } from '@/lib/playerPartyRosterLock'
import { resolvePlayerQueueLanes } from '@/lib/playerQueueLanes'
import { createCommunicationPolicyRefreshQueue } from '@/lib/playerCommunicationPolicy'
import {
  createPlayerMatchmakerSignalRuntime,
  type PlayerMatchmakerSignal,
} from '@/lib/playerMatchmakerSignals'
import { createPlayerNotificationDeliveryReporter } from '@/lib/playerNotificationDelivery'
import {
  createPlayerWebPushRuntime,
  initialPlayerWebPushState,
  type PlayerWebPushState,
} from '@/lib/playerWebPush'
import { formatLiveBroadcastCount, topbarSectionForRouteName } from '@/lib/topbarNavigation'
import { discordInviteURL } from '@/lib/communityLinks'
import {
  cancelCriticalPlayerSound,
  dispatchMatchConnectSoundOnce,
  dispatchReadyCheckAlertOnce,
  playMatchSearchCancelSound,
  playMatchSearchPanelCloseSound,
  playMatchSearchPanelOpenSound,
  playMatchSearchQueueChangeSound,
  playMatchSearchStartSound,
  playReadyCheckAcceptSound,
  playReadyCheckDeclineSound,
  preparePlayerSoundAssets,
  prepareReadyCheckAlert,
  reconcileCriticalPlayerSounds,
  setPlayerPushNotificationChannelActive,
  subscribePlayerSoundDelivery,
} from '@/lib/readyCheckAlert'

type ReadyCheckPlayer = NonNullable<PlayerMatchmakerState['ready_check']>['players'][number]
type ReadyCheckDisplaySlot = {
  index: number
  accepted: boolean
  avatarUrl: string
  label: string
}

const playerSessionSeenKey = 'dota688.player.session.seen'
const selectedQueueStorageKey = 'dota688.player.selectedQueueID'
const route = useRoute()
const playerSession = ref<PlayerMeResponse | null>(null)
const currentParty = ref<PlayerParty | null>(null)
const matchmakerState = ref<PlayerMatchmakerState | null>(null)
const sessionLoading = ref(false)
const sessionError = ref<string | null>(null)
const sessionExpired = ref(false)
const matchmakerLoading = ref(false)
const matchmakerActionPending = ref(false)
const partyDockBusy = ref(false)
const matchmakerError = ref<string | null>(null)
const searchPanelOpen = ref(false)
const playerDockModalOpen = ref(false)
const settingsMenuOpen = ref(false)
const mobileMenuOpen = ref(false)
const selectedQueueID = ref('')
const navRoot = ref<HTMLElement | null>(null)
const settingsRoot = ref<HTMLElement | null>(null)
const pageScroll = ref<HTMLElement | null>(null)
const readyCheckDialog = ref<HTMLElement | null>(null)
const findMatchButton = ref<HTMLButtonElement | null>(null)
const searchCancelButton = ref<HTMLButtonElement | null>(null)
const readyCheckAccept = ref<HTMLButtonElement | null>(null)
const readyCheckCollapse = ref<HTMLButtonElement | null>(null)
const playerProfileSocialActions = shallowRef<PlayerProfileSocialActionsController | null>(null)
const nowMs = ref(Date.now())
const monotonicNowMs = ref(performance.now())
const queueSearchTimerAnchor = ref<QueueSearchTimerAnchor | null>(null)
const queueLanesObservedAtMs = ref(performance.now())
const readyCheckAcceptedHoldMs = 2 * 1000
const matchmakerFetchTimeoutMS = 8 * 1000
const readyCheckHoldUntilMs = ref<number | null>(null)
const readyCheckHoldSnapshot = ref<{
  modeLabel: string
  acceptedCount: number
  totalCount: number
  slots: ReadyCheckDisplaySlot[]
} | null>(null)
const readyCheckCollapsed = ref(false)
const playerWebPushState = ref<PlayerWebPushState>({
  ...initialPlayerWebPushState,
})
const isAuthenticated = computed(() => Boolean(playerSession.value?.authenticated))
const playerAccessAllowed = computed(() => (
  isAuthenticated.value && playerSession.value?.access.allowed !== false
))
const playerAccessRestricted = computed(() => (
  isAuthenticated.value && !playerAccessAllowed.value
))
const verificationContactURL = computed(() => playerSession.value?.access.contact_url ?? '')
const currentPlayer = computed(() => playerSession.value?.player ?? null)
const currentPlayerIsModerator = computed(() => currentPlayer.value?.moderator === true)
// The one-time approval welcome: the server keeps asking for it until the
// player closes it once; the local flag hides it immediately on close.
const approvalWelcomeClosed = ref(false)
const showApprovalWelcome = computed(() => (
  isAuthenticated.value &&
  playerSession.value?.access.welcome_pending === true &&
  !approvalWelcomeClosed.value
))
function closeApprovalWelcome(): void {
  approvalWelcomeClosed.value = true
  void playerApi.dismissAccessWelcome().catch(() => {
    // Not recorded: the welcome shows once more on the next visit.
  })
}

provide(playerProfileSocialActionsKey, playerProfileSocialActions)
provide(playerHistoricalAccessAllowedKey, playerAccessAllowed)
provide(playerModeratorKey, currentPlayerIsModerator)
const playerChatCapabilities = computed(() => playerSession.value?.capabilities.chat ?? null)
const playerCommunicationRestrictions = computed(() => (
  playerSession.value?.restrictions?.communication ?? null
))
const playerCommunicationNextPolicyChangeAt = computed(() => (
  playerCommunicationRestrictions.value?.next_policy_change_at ?? null
))
const currentPartyCanStartMatchmaking = computed(() => canStartPartyMatchmaking(
  currentParty.value,
  currentPlayer.value?.steam_id ?? '',
))
const currentPartyCanCancelMatchmaking = computed(() => canCancelPartyMatchmaking(
  currentParty.value,
  currentPlayer.value?.steam_id ?? '',
))
const searchCancelLabel = computed(() => currentParty.value
  ? 'Отменить поиск для всей группы'
  : 'Отменить поиск')
const topbarState = computed(() => topbarSectionForRouteName(route.name))
const matchesNavActive = computed(() => topbarState.value === 'matches')
const playersNavActive = computed(() => topbarState.value === 'players')
const watchNavActive = computed(() => topbarState.value === 'watch')
const metaNavActive = computed(() => topbarState.value === 'meta')
const startNavActive = computed(() => topbarState.value === 'start')
const liveBroadcastCount = computed(() => publicLiveCountState.count.value)
const liveBroadcastCountLabel = computed(() => formatLiveBroadcastCount(liveBroadcastCount.value))
const liveNavAriaLabel = computed(() => liveBroadcastCount.value > 0
  ? `Смотреть, трансляций: ${liveBroadcastCount.value}`
  : 'Смотреть')
const activeQueue = computed(() => {
  const queues = matchmakerState.value?.queues ?? []
  return queues.find(q => q.enabled && !q.is_at_cap) ?? queues.find(q => q.enabled) ?? queues[0] ?? null
})
const selectedQueue = computed(() => {
  const queues = matchmakerState.value?.queues ?? []
  if ((matchmakerState.value?.status ?? 'idle') !== 'idle' && matchmakerState.value?.queue_id) {
    const stateQueue = queues.find(q => q.id === matchmakerState.value?.queue_id)
    if (stateQueue) return stateQueue
  }
  return queues.find(q => q.id === selectedQueueID.value) ?? activeQueue.value
})
const selectedQueueBan = computed(() => selectedQueue.value?.ban ?? null)
const selectedQueueBanNotice = computed(() => {
  const ban = selectedQueueBan.value
  return ban ? formatQueueBanNotice(ban) : ''
})
const currentPartySize = computed(() => currentParty.value?.members.length ?? 1)
const selectedQueuePartySizeBlocked = computed(() => {
  const queue = selectedQueue.value
  return Boolean(queue && !queueAllowsPartySize(queue, currentPartySize.value))
})
const selectedQueueNotice = computed(() => {
  if (selectedQueueBanNotice.value) return selectedQueueBanNotice.value
  if (selectedQueuePartySizeBlocked.value) {
    return `Очередь не принимает группу из ${currentPartySize.value} игроков.`
  }
  return ''
})
const currentQueueID = computed(() => {
  if (playerQueueStatus.value !== 'idle' && matchmakerState.value?.queue_id) {
    return matchmakerState.value.queue_id
  }
  return selectedQueue.value?.id || activeQueue.value?.id || ''
})
const playerQueueStatus = computed(() => matchmakerState.value?.status ?? 'idle')
const partyRosterLock = computed(() => resolvePlayerPartyRosterLock(matchmakerState.value))
const queueLanes = computed(() => resolvePlayerQueueLanes(matchmakerState.value, {
  canJoin: playerAccessAllowed.value &&
    currentPartyCanStartMatchmaking.value &&
    !playerServiceUnavailable.value,
  partySizeAllowed: queue => queueAllowsPartySize(
    queue as PlayerMatchmakerQueue,
    currentPartySize.value,
  ),
}))
const queueLaneBackground = computed(() => {
  const sinceFetchS = Math.max(
    0,
    Math.floor((monotonicNowMs.value - queueLanesObservedAtMs.value) / 1000),
  )
  return queueLanes.value.background.map(ticket => ({
    queueID: ticket.queueID,
    label: ticket.label,
    elapsed: formatQueueSearchElapsed(ticket.elapsedSeconds + sinceFetchS),
  }))
})
const showQueueLanes = computed(() => (
  !showReadyCheckOverlay.value &&
  (queueLaneBackground.value.length > 0 || queueLanes.value.offers.length > 0)
))
const currentMatch = computed(() => matchmakerState.value?.match ?? null)
const isSearching = computed(() => playerQueueStatus.value === 'queued')
const isReadyCheckActive = computed(() => playerQueueStatus.value === 'ready_check')
const isReadyCheckHoldActive = computed(() => {
  const until = readyCheckHoldUntilMs.value
  return Boolean(readyCheckHoldSnapshot.value && until !== null && nowMs.value < until)
})
const readyCheckInstanceKey = computed(() => {
  const state = matchmakerState.value
  const readyCheck = state?.ready_check
  if (!state || state.status !== 'ready_check' || !readyCheck) return ''
  const playerID = state.player?.steam_id || state.handle || 'player'
  const queueID = state.queue_id || 'queue'
  const startedAt = readyCheck.started_at || state.queued_at || 'pending'
  return `${playerID}:${queueID}:${startedAt}`
})
const isReadyCheckPending = computed(() => playerQueueStatus.value === 'ready_check' && !matchmakerState.value?.ready_check?.accepted)
const isReadyCheckAccepted = computed(() => playerQueueStatus.value === 'ready_check' && Boolean(matchmakerState.value?.ready_check?.accepted))
const readyCheckDisplayAccepted = computed(() => isReadyCheckAccepted.value || isReadyCheckHoldActive.value)
const canCollapseReadyCheck = computed(() => readyCheckDisplayAccepted.value && (isReadyCheckActive.value || isReadyCheckHoldActive.value))
const showReadyCheckCompact = computed(() => readyCheckCollapsed.value && canCollapseReadyCheck.value)
const showReadyCheckOverlay = computed(() => (isReadyCheckActive.value || isReadyCheckHoldActive.value) && !showReadyCheckCompact.value)
const actionBusy = computed(() => (
  sessionLoading.value ||
  matchmakerActionPending.value ||
  partyDockBusy.value
))
const playerServiceUnavailable = computed(() => Boolean(sessionError.value || matchmakerError.value))
const selectedQueueJoinBlocked = computed(() => {
  const queue = selectedQueue.value
  return (
    !queue ||
    !queue.enabled ||
    queue.is_at_cap ||
    Boolean(queue.ban) ||
    selectedQueuePartySizeBlocked.value
  )
})
const canOpenSearchPanel = computed(() => {
  return playerAccessAllowed.value &&
    currentPartyCanStartMatchmaking.value &&
    !playerServiceUnavailable.value &&
    !actionBusy.value &&
    playerQueueStatus.value === 'idle' &&
    (matchmakerState.value?.queues?.length ?? 0) > 0
})
const canLeaveCurrentMatch = computed(() => {
  return playerQueueStatus.value === 'in_match' && Boolean(currentMatch.value?.can_leave)
})
const currentMatchLeaveState = computed(() => normalizeMatchLeaveState(currentMatch.value?.leave_state))
const matchConnectHost = computed(() => {
  if (playerQueueStatus.value !== 'in_match') return ''
  return currentMatch.value?.connect_host?.trim() || ''
})
const canConnectCurrentMatch = computed(() => {
  return Boolean(matchConnectHost.value) && isPreConnectionMatchState(currentMatchLeaveState.value)
})
const canReconnectCurrentMatch = computed(() => {
  return Boolean(matchConnectHost.value) && currentMatchLeaveState.value === 'disconnected'
})
const showMatchConnectAction = computed(() => canConnectCurrentMatch.value || canReconnectCurrentMatch.value)
const showPrimaryAction = computed(() => {
  if (playerAccessRestricted.value) {
    return !showReadyCheckCompact.value && playerQueueStatus.value === 'idle'
  }
  if (showReadyCheckCompact.value) return false
  if (playerQueueStatus.value !== 'in_match') return true
  return !showMatchConnectAction.value
})
const matchLeaveNoticeTone = computed(() => {
  if (!canLeaveCurrentMatch.value) return ''
  return currentMatch.value?.safe_to_leave ? 'safe' : 'danger'
})
const matchLeaveNoticeTitle = computed(() => {
  if (!canLeaveCurrentMatch.value) return ''
  return currentMatch.value?.safe_to_leave
    ? 'Эту игру можно спокойно покинуть.'
    : 'Выход сейчас считается ливом.'
})
const matchLeaveNoticeScore = computed(() => {
  if (!canLeaveCurrentMatch.value) return ''
  return `Зачет: ${matchScoreStateLabel(currentMatch.value?.score_state)}`
})
const connectCommand = computed(() => {
  if (!showMatchConnectAction.value || !matchConnectHost.value) return ''
  return `connect ${matchConnectHost.value}`
})
const matchConnectBaseLabel = computed(() => {
  return canReconnectCurrentMatch.value ? 'Reconnect' : 'Connect'
})
const currentQueueTitle = computed(() => {
  const queue = selectedQueue.value
  return (queue?.label || queue?.id || 'Queue').trim()
})
const currentModeLabel = computed(() => {
  const contract = selectedQueue.value?.active_contract
  if (!contract) return 'Match'
  return gameModeLabel(contract.game_mode, contract.label, contract.name)
})
const currentQueueProgressLabel = computed(() => {
  const queue = selectedQueue.value
  const cap = queue?.active_contract?.cap ?? 0
  if (!queue || cap <= 0) return ''
  return `(${queue.queue_count}/${cap})`
})
const readyCheckModeLabel = computed(() => {
  return currentModeLabel.value.toUpperCase()
})
const readyCheckTotalCount = computed(() => {
  const fromReadyCheck = matchmakerState.value?.ready_check?.total_count ?? 0
  const fromContract = selectedQueue.value?.active_contract?.cap ?? 0
  return Math.max(1, fromReadyCheck || fromContract || 10)
})
const readyCheckAcceptedCount = computed(() => {
  const ready = matchmakerState.value?.ready_check
  const count = ready?.accepted_count ?? (ready?.accepted ? 1 : 0)
  return Math.max(0, Math.min(readyCheckTotalCount.value, count))
})
const visibleReadyCheckModeLabel = computed(() => {
  if (isReadyCheckActive.value) return readyCheckModeLabel.value
  return readyCheckHoldSnapshot.value?.modeLabel ?? readyCheckModeLabel.value
})
const visibleReadyCheckTotalCount = computed(() => {
  if (isReadyCheckActive.value) return readyCheckTotalCount.value
  return readyCheckHoldSnapshot.value?.totalCount ?? readyCheckTotalCount.value
})
const visibleReadyCheckAcceptedCount = computed(() => {
  const count = isReadyCheckActive.value
    ? readyCheckAcceptedCount.value
    : readyCheckHoldSnapshot.value?.acceptedCount ?? 0
  return Math.max(0, Math.min(visibleReadyCheckTotalCount.value, count))
})
const visibleReadyCheckSlots = computed(() => {
  if (isReadyCheckActive.value) {
    return buildReadyCheckDisplaySlots(
      readyCheckTotalCount.value,
      matchmakerState.value?.ready_check?.players ?? [],
      readyCheckAcceptedCount.value,
    )
  }
  return readyCheckHoldSnapshot.value?.slots ?? buildReadyCheckDisplaySlots(
    visibleReadyCheckTotalCount.value,
    [],
    visibleReadyCheckAcceptedCount.value,
  )
})
const searchElapsedLabel = computed(() => {
  return formatQueueSearchElapsed(
    queueSearchElapsedSeconds(queueSearchTimerAnchor.value, monotonicNowMs.value),
  )
})
const actionLabel = computed(() => {
  if (searchPanelOpen.value && selectedQueueBan.value) return 'Blocked'
  if (searchPanelOpen.value && selectedQueuePartySizeBlocked.value) return 'Размер группы'
  if (searchPanelOpen.value) return 'Find Match'
  if (playerServiceUnavailable.value) return 'Offline'
  if (actionBusy.value) return 'Sync'
  if (sessionExpired.value && !isAuthenticated.value) return 'Войти снова'
  if (!isAuthenticated.value) return 'Steam Login'
  if (!playerAccessAllowed.value && playerQueueStatus.value === 'queued') return 'Отменить поиск'
  if (!playerAccessAllowed.value) return 'Нужно подтверждение'
  if (!activeQueue.value && playerQueueStatus.value === 'idle') return 'No Queue'
  if (playerQueueStatus.value === 'queued') return 'Отменить поиск'
  if (isReadyCheckPending.value) return 'Accept'
  if (isReadyCheckAccepted.value) return 'Accepted'
  if (playerQueueStatus.value === 'in_match') return 'In Match'
  return 'Play Dota'
})
const actionDisabled = computed(() => {
  if (playerServiceUnavailable.value) return true
  if (actionBusy.value) return true
  if (!isAuthenticated.value) return false
  if (!playerAccessAllowed.value) {
    return playerQueueStatus.value !== 'queued'
  }
  if (playerQueueStatus.value === 'idle' && !currentPartyCanStartMatchmaking.value) return true
  if (playerQueueStatus.value === 'queued' && !currentPartyCanCancelMatchmaking.value) return true
  if (playerQueueStatus.value === 'idle') {
    if (searchPanelOpen.value) return selectedQueueJoinBlocked.value
    return !selectedQueue.value
  }
  return isReadyCheckAccepted.value || playerQueueStatus.value === 'in_match'
})
let matchmakerPoll: number | undefined
let playerSessionPoll: number | undefined
let communicationPolicyRefreshTimer: number | undefined
let clockTimer: number | undefined
let matchmakerStateRequestID = 0
let matchmakerStateAbort: AbortController | null = null
let playerSessionStatusRequestID = 0
let readyCheckFocusGeneration = 0
let readyCheckReturnFocusTarget: HTMLElement | null = null
let playerWebPushStarted = false
const communicationPolicyLiveRefreshQueue = createCommunicationPolicyRefreshQueue({
  authenticated: () => isAuthenticated.value,
  blocked: () => sessionLoading.value,
  refresh: refreshPlayerSessionStatus,
})
const matchmakerSignalRefreshQueue = createCommunicationPolicyRefreshQueue({
  authenticated: () => isAuthenticated.value,
  blocked: () => sessionLoading.value || matchmakerActionPending.value,
  refresh: () => loadPlayerMatchmakerState(),
})
const matchmakerSignalRuntime = createPlayerMatchmakerSignalRuntime({
  authenticated: () => isAuthenticated.value,
  onSignal: handlePlayerMatchmakerSignal,
})
const reportPlayerSoundDelivery = createPlayerNotificationDeliveryReporter({
  authenticated: () => isAuthenticated.value,
  actorKey: () => currentPlayer.value?.steam_id ?? '',
})
const stopPlayerSoundDeliveryTelemetry = subscribePlayerSoundDelivery(
  reportPlayerSoundDelivery,
)
const playerWebPushRuntime = createPlayerWebPushRuntime({
  authenticated: () => isAuthenticated.value,
  actorKey: () => currentPlayer.value?.steam_id ?? '',
  onState: state => {
    playerWebPushState.value = state
  },
  onChannelActive: setPlayerPushNotificationChannelActive,
  onOpen: () => {
    if (isAuthenticated.value && !sessionLoading.value) {
      void loadPlayerMatchmakerState()
    }
  },
})
const stopMatchmakerSignalWatch = watch(
  [isAuthenticated, sessionLoading],
  ([authenticated, loading]) => {
    if (authenticated && !loading) {
      matchmakerSignalRuntime.start()
    } else {
      matchmakerSignalRuntime.stop()
    }
  },
  { immediate: true, flush: 'sync' },
)
const stopPlayerWebPushWatch = watch(
  [isAuthenticated, () => currentPlayer.value?.steam_id ?? '', sessionLoading],
  ([, , loading]) => {
    if (playerWebPushStarted && !loading) void playerWebPushRuntime.sync()
  },
  { flush: 'post' },
)

const gameModeLabels: Record<number, string> = {
  1: 'All Pick',
  2: 'Captains Mode',
  3: 'Random Draft',
  4: 'Single Draft',
  5: 'All Random',
  11: 'Mid Only',
  16: 'Captains Draft',
  17: 'Balanced Draft',
  18: 'Ability Draft',
  20: 'All Random Death Match',
  21: '1v1 Mid Only',
  22: 'Ranked All Pick',
}

function gameModeLabel(gameMode: number, label?: string, name?: string): string {
  return gameModeLabels[gameMode] || label || name || `Mode ${gameMode}`
}

// Decorative WebGL scene: its chunk and assets load only once the page is idle.
const DashboardBackdrop = defineAsyncComponent(() => import('@/components/DashboardBackdrop.vue'))
const backdropEnabled = ref(false)
let cancelBackdropIdle: (() => void) | undefined

onMounted(() => {
  if (backdropSupported()) cancelBackdropIdle = whenIdle(() => { backdropEnabled.value = true })
})

onUnmounted(() => cancelBackdropIdle?.())

onMounted(async () => {
  selectedQueueID.value = loadSelectedQueueID()
  preparePlayerSoundAssets()
  startPublicStatusPolling()
  startPublicLiveCount()
  await loadPlayerSession()
  playerWebPushStarted = true
  void playerWebPushRuntime.start()
  matchmakerPoll = window.setInterval(() => {
    if (!document.hidden && shouldRefreshMatchmakerState() && !matchmakerLoading.value && !matchmakerActionPending.value) {
      void loadPlayerMatchmakerState()
    }
  }, 5000)
  playerSessionPoll = window.setInterval(() => {
    if (!document.hidden && isAuthenticated.value && !sessionLoading.value) {
      void refreshPlayerSessionStatus()
    }
  }, 15000)
  clockTimer = window.setInterval(() => {
    if (!document.hidden) {
      nowMs.value = Date.now()
      monotonicNowMs.value = performance.now()
    }
  }, 1000)
  document.addEventListener('visibilitychange', handleAppVisibilityChange)
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('pointerdown', handlePointerDown)
})

onUnmounted(() => {
  stopPublicStatusPolling()
  stopPublicLiveCount()
  if (matchmakerPoll !== undefined) {
    window.clearInterval(matchmakerPoll)
  }
  if (playerSessionPoll !== undefined) {
    window.clearInterval(playerSessionPoll)
  }
  if (communicationPolicyRefreshTimer !== undefined) {
    window.clearTimeout(communicationPolicyRefreshTimer)
  }
  communicationPolicyLiveRefreshQueue.dispose()
  stopMatchmakerSignalWatch()
  matchmakerSignalRefreshQueue.dispose()
  matchmakerSignalRuntime.dispose()
  stopPlayerSoundDeliveryTelemetry()
  stopPlayerWebPushWatch()
  playerWebPushRuntime.dispose()
  cancelMatchmakerStateRequest()
  if (clockTimer !== undefined) {
    window.clearInterval(clockTimer)
  }
  document.removeEventListener('visibilitychange', handleAppVisibilityChange)
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('pointerdown', handlePointerDown)
})

function handleAppVisibilityChange(): void {
  if (document.hidden) return
  nowMs.value = Date.now()
  monotonicNowMs.value = performance.now()
  if (isAuthenticated.value && !sessionLoading.value) {
    void refreshPlayerSessionStatus()
  }
}

function handlePlayerMatchmakerSignal(signal: PlayerMatchmakerSignal): void {
  if (signal.reason === 'resume' || signal.reason === 'visible') {
    void loadPlayerMatchmakerState()
    return
  }
  matchmakerSignalRefreshQueue.request()
}

watch(matchmakerState, state => {
  reconcileCriticalPlayerSounds({
    readyCheckEventKey: state ? readyCheckAlertKey(state) : null,
    matchConnectEventKey: state ? matchConnectSoundKey(state) : null,
  })
  syncSelectedQueue()
  const observedAtMonotonicMs = performance.now()
  queueSearchTimerAnchor.value = reconcileQueueSearchTimer(
    queueSearchTimerAnchor.value,
    state,
    observedAtMonotonicMs,
    Date.now(),
  )
  monotonicNowMs.value = observedAtMonotonicMs
}, { flush: 'sync' })

watch(playerCommunicationNextPolicyChangeAt, scheduleCommunicationPolicyRefresh, {
  immediate: true,
})

function scheduleCommunicationPolicyRefresh(nextChange: string | null): void {
  if (communicationPolicyRefreshTimer !== undefined) {
    window.clearTimeout(communicationPolicyRefreshTimer)
    communicationPolicyRefreshTimer = undefined
  }
  if (!nextChange) return
  const changeAt = Date.parse(nextChange)
  if (!Number.isFinite(changeAt)) return
  const delay = Math.max(0, changeAt - Date.now() + 250)
  communicationPolicyRefreshTimer = window.setTimeout(() => {
    communicationPolicyRefreshTimer = undefined
    if (changeAt > Date.now()) {
      scheduleCommunicationPolicyRefresh(nextChange)
      return
    }
    if (!isAuthenticated.value) return
    if (sessionLoading.value) {
      communicationPolicyRefreshTimer = window.setTimeout(() => {
        communicationPolicyRefreshTimer = undefined
        scheduleCommunicationPolicyRefresh(nextChange)
      }, 1000)
      return
    }
    void refreshPlayerSessionStatus()
  }, Math.min(delay, 2147483647))
}

watch(playerQueueStatus, status => {
  if (status !== 'idle') {
    searchPanelOpen.value = false
  }
  if (status !== 'ready_check') {
    readyCheckCollapsed.value = false
  }
  if (status === 'idle' || status === 'queued') {
    clearReadyCheckHold()
  }
})

watch(readyCheckInstanceKey, (next, prev) => {
  if (next !== prev) {
    readyCheckCollapsed.value = false
  }
})

watch(
  [showReadyCheckOverlay, readyCheckDisplayAccepted],
  async ([open, accepted], [wasOpen]) => {
    const generation = ++readyCheckFocusGeneration
    if (open) {
      if (!wasOpen) {
        const activeElement = document.activeElement
        readyCheckReturnFocusTarget = activeElement instanceof HTMLElement &&
          activeElement !== document.body &&
          activeElement !== document.documentElement
          ? activeElement
          : null
      } else if (
        document.activeElement instanceof Node &&
        readyCheckDialog.value?.contains(document.activeElement)
      ) {
        // Move focus off the face that is about to become inert.
        readyCheckDialog.value.focus({ preventScroll: true })
      }
      await nextTick()
      if (generation !== readyCheckFocusGeneration || !showReadyCheckOverlay.value) return
      const target = accepted
        ? readyCheckCollapse.value ?? readyCheckDialog.value
        : readyCheckAccept.value?.disabled
          ? readyCheckDialog.value
          : readyCheckAccept.value
      target?.focus({ preventScroll: true })
      return
    }
    if (!wasOpen) return
    await nextTick()
    if (generation !== readyCheckFocusGeneration || showReadyCheckOverlay.value) return
    restoreReadyCheckFocus()
  },
)

watch(() => route.fullPath, () => {
  closeMobileMenu()
  pageScroll.value?.scrollTo({ top: 0, left: 0 })
}, { flush: 'post' })

function restoreReadyCheckFocus(): void {
  const previous = readyCheckReturnFocusTarget
  readyCheckReturnFocusTarget = null
  if (isFocusableReadyCheckReturnTarget(previous)) {
    previous.focus({ preventScroll: true })
    return
  }
  const fallback = document.querySelector<HTMLElement>(
    '.ready-check-compact, .match-connect:not(:disabled), .find-match:not(:disabled), .player-notification-trigger:not(:disabled), .profile-chip:not(:disabled), .dnav-brand-link',
  )
  if (isFocusableReadyCheckReturnTarget(fallback)) {
    fallback.focus({ preventScroll: true })
  }
}

function isFocusableReadyCheckReturnTarget(target: HTMLElement | null): target is HTMLElement {
  if (
    !target?.isConnected ||
    target === document.body ||
    target === document.documentElement ||
    target.closest('[inert]')
  ) {
    return false
  }
  return !(target instanceof HTMLButtonElement && target.disabled)
}

function activateReadyCheckDialog(element: Element): void {
  if (element instanceof HTMLElement) element.inert = false
}

function deactivateReadyCheckDialog(element: Element): void {
  if (element instanceof HTMLElement) element.inert = true
}

async function loadPlayerSession(): Promise<void> {
  playerSessionStatusRequestID += 1
  sessionLoading.value = true
  sessionError.value = null
  try {
    playerSession.value = await playerApi.me()
    if (playerSession.value?.authenticated) {
      markPlayerSessionSeen()
      sessionExpired.value = false
      await loadPlayerMatchmakerState()
    } else {
      currentParty.value = null
      matchmakerState.value = null
      searchPanelOpen.value = false
      clearReadyCheckHold()
    }
  } catch (error) {
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    sessionError.value = error instanceof Error ? error.message : String(error)
    playerSession.value = null
    currentParty.value = null
    matchmakerState.value = null
    searchPanelOpen.value = false
    clearReadyCheckHold()
  } finally {
    sessionLoading.value = false
  }
}

async function refreshPlayerSessionStatus(): Promise<void> {
  if (!isAuthenticated.value || sessionLoading.value) return
  const actorSteamID = currentPlayer.value?.steam_id ?? ''
  const requestID = ++playerSessionStatusRequestID
  try {
    const next = await playerApi.me()
    if (
      requestID !== playerSessionStatusRequestID ||
      !isAuthenticated.value ||
      actorSteamID !== (currentPlayer.value?.steam_id ?? '')
    ) {
      return
    }
    if (!next.authenticated) {
      handleUnauthorizedPlayerSession()
      return
    }
    const wasAllowed = playerAccessAllowed.value
    playerSession.value = next
    sessionExpired.value = false
    if (!playerAccessAllowed.value) {
      searchPanelOpen.value = false
    }
    if (
      wasAllowed !== playerAccessAllowed.value &&
      !matchmakerLoading.value &&
      !matchmakerActionPending.value
    ) {
      await loadPlayerMatchmakerState()
    }
  } catch (error) {
    if (requestID !== playerSessionStatusRequestID) return
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
    }
  }
}

async function loadPlayerMatchmakerState(): Promise<void> {
  const requestID = ++matchmakerStateRequestID
  matchmakerStateAbort?.abort()
  matchmakerStateAbort = null
  if (!shouldRefreshMatchmakerState()) {
    matchmakerState.value = null
    matchmakerLoading.value = false
    clearReadyCheckHold()
    return
  }
  const controller = new AbortController()
  matchmakerStateAbort = controller
  const requestStartedAt = Date.now()
  let timedOut = false
  const timeout = window.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, matchmakerFetchTimeoutMS)
  matchmakerLoading.value = true
  matchmakerError.value = null
  try {
    const nextState = await playerApi.matchmakerState(controller.signal)
    if (requestID !== matchmakerStateRequestID || !isAuthenticated.value) return
    matchmakerState.value = nextState
    queueLanesObservedAtMs.value = performance.now()
    syncReadyCheckHoldWithState()
    dispatchReadyCheckAlertForState(nextState, requestStartedAt)
    dispatchMatchConnectSoundForState(nextState)
  } catch (error) {
    if (requestID !== matchmakerStateRequestID) return
    if (isAbortError(error) && !timedOut) return
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    matchmakerError.value = timedOut
      ? 'Не удалось вовремя обновить состояние поиска.'
      : error instanceof Error ? error.message : String(error)
  } finally {
    window.clearTimeout(timeout)
    if (matchmakerStateAbort === controller) matchmakerStateAbort = null
    if (requestID === matchmakerStateRequestID) {
      matchmakerLoading.value = false
    }
  }
}

function isAbortError(error: unknown): boolean {
  return typeof DOMException !== 'undefined' && error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError'
}

function cancelMatchmakerStateRequest(): void {
  matchmakerStateRequestID += 1
  matchmakerStateAbort?.abort()
  matchmakerStateAbort = null
  matchmakerLoading.value = false
}

function handlePlayerPartyState(party: PlayerParty | null): void {
  currentParty.value = party
  if (!currentPartyCanStartMatchmaking.value) {
    closeSearchPanel(false)
  }
}

function handlePlayerPartyChanged(): void {
  if (shouldRefreshMatchmakerState() && !matchmakerLoading.value && !matchmakerActionPending.value) {
    void loadPlayerMatchmakerState()
  }
}

function handlePlayerPartyBusy(busy: boolean): void {
  partyDockBusy.value = busy
}

function handlePlayerDockModal(open: boolean): void {
  playerDockModalOpen.value = open
}

function loginSteam(): void {
  window.location.assign(playerApi.loginURL())
}

function handleUnauthorizedPlayerSession(): void {
  playerSessionStatusRequestID += 1
  cancelMatchmakerStateRequest()
  sessionExpired.value = hasSeenPlayerSession()
  sessionError.value = null
  matchmakerError.value = null
  playerSession.value = null
  currentParty.value = null
  matchmakerState.value = null
  searchPanelOpen.value = false
  clearReadyCheckHold()
}

function handlePlayerAccessRequired(): void {
  searchPanelOpen.value = false
  matchmakerError.value = null
  void refreshPlayerSessionStatus()
}

function shouldRefreshMatchmakerState(): boolean {
  return isAuthenticated.value
}

function hasSeenPlayerSession(): boolean {
  try {
    return window.localStorage.getItem(playerSessionSeenKey) === '1'
  } catch {
    return false
  }
}

function markPlayerSessionSeen(): void {
  try {
    window.localStorage.setItem(playerSessionSeenKey, '1')
  } catch {
    // localStorage can be unavailable in private or locked-down contexts.
  }
}

function clearPlayerSessionSeen(): void {
  try {
    window.localStorage.removeItem(playerSessionSeenKey)
  } catch {
    // localStorage can be unavailable in private or locked-down contexts.
  }
}

async function logout(): Promise<void> {
  playerSessionStatusRequestID += 1
  sessionLoading.value = true
  sessionError.value = null
  try {
    await playerWebPushRuntime.disable({ bestEffort: true })
    await playerApi.logout()
    settingsMenuOpen.value = false
    clearPlayerSessionSeen()
    sessionExpired.value = false
    playerSession.value = null
    currentParty.value = null
    matchmakerState.value = null
    clearReadyCheckHold()
  } catch (error) {
    sessionError.value = error instanceof Error ? error.message : String(error)
  } finally {
    sessionLoading.value = false
  }
}

function enablePlayerWebPush(): void {
  void playerWebPushRuntime.enable()
}

function disablePlayerWebPush(): void {
  void playerWebPushRuntime.disable()
}

function toggleSettingsMenu(): void {
  settingsMenuOpen.value = !settingsMenuOpen.value
}

function closeSettingsMenu(): void {
  settingsMenuOpen.value = false
}

function toggleMobileMenu(): void {
  mobileMenuOpen.value = !mobileMenuOpen.value
  if (mobileMenuOpen.value) {
    closeSettingsMenu()
  }
}

function closeMobileMenu(): void {
  mobileMenuOpen.value = false
}

function closePlayerExternalSurfaces(): void {
  closeSearchPanel(false)
  closeSettingsMenu()
  closeMobileMenu()
}

function handleFindMatch(): void {
  if (!isAuthenticated.value) {
    loginSteam()
    return
  }
  if (!playerAccessAllowed.value && playerQueueStatus.value !== 'queued') return
  if (searchPanelOpen.value) {
    void runMatchmakerAction()
    return
  }
  if (playerQueueStatus.value === 'idle') {
    openSearchPanel()
    return
  }
  void runMatchmakerAction()
}

async function leaveCurrentMatch(): Promise<void> {
  if (!canLeaveCurrentMatch.value || actionBusy.value) return
  if (!confirmLeaveCurrentMatch()) return
  matchmakerActionPending.value = true
  matchmakerLoading.value = true
  matchmakerError.value = null
  try {
    await assertResult(playerApi.leaveMatch())
    await loadPlayerMatchmakerState()
  } catch (error) {
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    matchmakerError.value = error instanceof Error ? error.message : String(error)
  } finally {
    matchmakerActionPending.value = false
    matchmakerLoading.value = false
  }
}

function confirmLeaveCurrentMatch(): boolean {
  const message = currentMatch.value?.safe_to_leave
    ? 'Ливнуть из матча? После подтверждения эта кнопка исчезнет, а матч останется занятым до завершения.'
    : 'Ливнуть из матча? Это действие засчитает abandon и может выдать временный бан.'
  return window.confirm(message)
}

async function runMatchmakerAction(): Promise<void> {
  if (actionDisabled.value) return
  matchmakerActionPending.value = true
  matchmakerLoading.value = true
  matchmakerError.value = null
  try {
    if (playerQueueStatus.value === 'queued') {
      playMatchSearchCancelSound()
      await assertResult(playerApi.leaveQueue(currentQueueID.value))
    } else if (isReadyCheckPending.value) {
      const state = matchmakerState.value
      const eventKey = state ? readyCheckAlertKey(state) : null
      if (eventKey) cancelCriticalPlayerSound('ready_check', eventKey)
      prepareReadyCheckAlert()
      playReadyCheckAcceptSound()
      await assertResult(playerApi.acceptReadyCheck())
      startReadyCheckAcceptedHold()
    } else {
      prepareReadyCheckAlert()
      playMatchSearchStartSound()
      await assertResult(playerApi.joinQueue(currentQueueID.value))
    }
    await loadPlayerMatchmakerState()
    searchPanelOpen.value = false
    if (playerQueueStatus.value === 'queued' || playerQueueStatus.value === 'idle') {
      void nextTick(() => restoreSearchPanelFocus(playerQueueStatus.value === 'queued'))
    }
  } catch (error) {
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    matchmakerError.value = error instanceof Error ? error.message : String(error)
  } finally {
    matchmakerActionPending.value = false
    matchmakerLoading.value = false
  }
}

// Background lane actions touch only the requested queue; the main activity
// (a 1v1 match, a 5v5 search) keeps running.
async function runQueueLaneAction(queueID: string, join: boolean): Promise<void> {
  if (actionBusy.value || !queueID) return
  matchmakerActionPending.value = true
  matchmakerLoading.value = true
  matchmakerError.value = null
  try {
    if (join) {
      prepareReadyCheckAlert()
      playMatchSearchStartSound()
      await assertResult(playerApi.joinQueue(queueID))
    } else {
      playMatchSearchCancelSound()
      await assertResult(playerApi.leaveQueue(queueID))
    }
    await loadPlayerMatchmakerState()
  } catch (error) {
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    matchmakerError.value = error instanceof Error ? error.message : String(error)
  } finally {
    matchmakerActionPending.value = false
    matchmakerLoading.value = false
  }
}

function openSearchPanel(): void {
  syncSelectedQueue()
  if (!canOpenSearchPanel.value) return
  if (!searchPanelOpen.value) {
    playMatchSearchPanelOpenSound()
  }
  searchPanelOpen.value = true
}

function closeSearchPanel(restoreFocus = true): void {
  const wasOpen = searchPanelOpen.value
  if (wasOpen) {
    playMatchSearchPanelCloseSound()
  }
  searchPanelOpen.value = false
  if (wasOpen && restoreFocus) {
    void nextTick(() => restoreSearchPanelFocus(false))
  }
}

function restoreSearchPanelFocus(preferCancel: boolean): void {
  const preferred = preferCancel
    ? searchCancelButton.value
    : findMatchButton.value
  if (isFocusableReadyCheckReturnTarget(preferred)) {
    preferred.focus({ preventScroll: true })
    return
  }
  const fallback = document.querySelector<HTMLElement>(
    '.search-status-main button:not(:disabled), .find-match:not(:disabled), .party-strip-slot:not(:disabled), .player-notification-trigger:not(:disabled), .profile-chip, .dnav-brand-link',
  )
  if (isFocusableReadyCheckReturnTarget(fallback)) {
    fallback.focus({ preventScroll: true })
  }
}

function collapseReadyCheck(): void {
  if (canCollapseReadyCheck.value) {
    readyCheckCollapsed.value = true
  }
}

function expandReadyCheck(): void {
  readyCheckCollapsed.value = false
}

function selectQueue(queueID: string): void {
  if (queueID !== selectedQueueID.value) {
    playMatchSearchQueueChangeSound()
  }
  setSelectedQueueID(queueID)
}

function syncSelectedQueue(): void {
  const queues = matchmakerState.value?.queues ?? []
  if (queues.length === 0) return
  if (selectedQueueID.value && queues.some(queue => queue.id === selectedQueueID.value)) return
  const stateQueueID = matchmakerState.value?.queue_id
  const stateQueue = stateQueueID ? queues.find(queue => queue.id === stateQueueID) : null
  setSelectedQueueID((
    stateQueue ??
    queues.find(queue => queue.enabled && !queue.is_at_cap) ??
    queues.find(queue => queue.enabled) ??
    queues[0]
  ).id)
}

function setSelectedQueueID(queueID: string): void {
  selectedQueueID.value = queueID
  try {
    if (queueID) {
      window.localStorage.setItem(selectedQueueStorageKey, queueID)
    } else {
      window.localStorage.removeItem(selectedQueueStorageKey)
    }
  } catch {
    // localStorage can be unavailable in private or locked-down contexts.
  }
}

function loadSelectedQueueID(): string {
  try {
    return window.localStorage.getItem(selectedQueueStorageKey) || ''
  } catch {
    return ''
  }
}

function dispatchReadyCheckAlertForState(
  state: PlayerMatchmakerState,
  requestStartedAt: number,
): void {
  const eventKey = readyCheckAlertKey(state)
  if (!eventKey) return
  const observedAt = Date.now()
  const remainingSeconds = state.ready_check?.pending_deadline_s ??
    state.ready_check?.seconds_remaining
  dispatchReadyCheckAlertOnce(eventKey, modeLabelForState(state), {
    observedAt,
    ...(state.ready_check?.started_at
      ? { serverEventAt: state.ready_check.started_at }
      : {}),
    ...(typeof remainingSeconds === 'number' && Number.isFinite(remainingSeconds)
      ? { expiresAt: requestStartedAt + Math.max(0, remainingSeconds) * 1000 }
      : {}),
  })
}

function dispatchMatchConnectSoundForState(state: PlayerMatchmakerState): void {
  const eventKey = matchConnectSoundKey(state)
  if (!eventKey) return
  dispatchMatchConnectSoundOnce(eventKey, {
    observedAt: Date.now(),
    ...(state.match?.started_at
      ? { serverEventAt: state.match.started_at }
      : {}),
  })
}

function readyCheckAlertKey(state: PlayerMatchmakerState): string | null {
  const readyCheck = state.ready_check
  if (state.status !== 'ready_check' || !readyCheck || readyCheck.accepted) return null

  const playerID = state.player.steam_id || state.handle || 'player'
  const queueID = state.queue_id || 'queue'
  const startedAt = readyCheck.started_at || state.queued_at || 'pending'
  return `${playerID}:${queueID}:${startedAt}`
}

function matchConnectSoundKey(state: PlayerMatchmakerState): string | null {
  const host = state.match?.connect_host?.trim()
  if (state.status !== 'in_match' || !state.match || !host) return null
  const leaveState = normalizeMatchLeaveState(state.match.leave_state)
  if (!isPreConnectionMatchState(leaveState) && leaveState !== 'disconnected') return null

  const playerID = state.player.steam_id || state.handle || 'player'
  const startedAt = state.match.started_at || state.slot_id || state.queue_id || 'match'
  return `${playerID}:${startedAt}:${host}`
}

function normalizeMatchLeaveState(leaveState?: string | null): string {
  return (leaveState || '').trim().toLowerCase()
}

function isPreConnectionMatchState(leaveState: string): boolean {
  return leaveState === '' ||
    leaveState === 'unknown' ||
    leaveState === 'not_yet_connected' ||
    leaveState === 'loading'
}

function matchScoreStateLabel(scoreState?: string | null): string {
  switch ((scoreState || '').trim()) {
    case 'will_count':
      return 'засчитается'
    case 'not_counted':
      return 'не засчитается'
    case 'pending':
      return 'пока не решен'
    case 'unknown':
    case '':
      return 'неизвестен'
    default:
      return 'неизвестен'
  }
}

function modeLabelForState(state: PlayerMatchmakerState): string {
  const queue = state.queue_id
    ? state.queues.find(candidate => candidate.id === state.queue_id)
    : selectedQueue.value
  const contract = queue?.active_contract
  if (!contract) return 'Ready Check'
  return gameModeLabel(contract.game_mode, contract.label, contract.name)
}

function formatQueueBanNotice(ban: PlayerQueueBan): string {
  const parts = [`Поиск недоступен: ${queueBanReasonLabel(ban.reason)}`]
  parts.push(ban.expires_at ? `До: ${formatQueueBanExpiry(ban.expires_at)}` : 'Срок: навсегда')
  return parts.join(' · ')
}

function queueBanReasonLabel(reason?: string | null): string {
  switch ((reason || '').trim()) {
    case 'ready_check_decline':
      return 'отклонение ready-check'
    case 'ready_check_timeout':
      return 'ready-check не был принят'
    case 'connect_timeout':
      return 'игрок не подключился к матчу'
    case 'match_abandon':
      return 'лив/abandon в матче'
    case '':
      return 'бан выдан администратором'
    default:
      return reason || 'бан выдан администратором'
  }
}

function formatQueueBanExpiry(raw: string): string {
  const parsed = Date.parse(raw)
  if (!Number.isFinite(parsed)) return raw
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(parsed))
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && mobileMenuOpen.value) {
    closeMobileMenu()
    return
  }
  if (event.key === 'Escape' && settingsMenuOpen.value) {
    closeSettingsMenu()
    return
  }
  if (event.key === 'Escape' && searchPanelOpen.value) {
    closeSearchPanel()
  }
}

function trapReadyCheckFocus(event: KeyboardEvent): void {
  if (event.key !== 'Tab') return
  const dialog = readyCheckDialog.value
  if (!dialog) return
  const focusable = [...dialog.querySelectorAll<HTMLElement>(
    'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
  )].filter(element => !element.closest('[inert]'))
  if (focusable.length === 0) {
    event.preventDefault()
    dialog.focus()
    return
  }
  const active = document.activeElement
  const activeIndex = active instanceof HTMLElement
    ? focusable.indexOf(active)
    : -1
  const destination = focusTrapDestination(activeIndex, focusable.length, event.shiftKey)
  if (destination === null) return
  event.preventDefault()
  focusable[destination].focus()
}

function handlePointerDown(event: PointerEvent): void {
  const target = event.target
  if (!(target instanceof Node)) return
  if (settingsMenuOpen.value && !settingsRoot.value?.contains(target)) {
    closeSettingsMenu()
  }
  if (mobileMenuOpen.value && !navRoot.value?.contains(target)) {
    closeMobileMenu()
  }
}

async function declineReadyCheck(): Promise<void> {
  if (actionBusy.value) return
  matchmakerActionPending.value = true
  matchmakerLoading.value = true
  matchmakerError.value = null
  try {
    const state = matchmakerState.value
    const eventKey = state ? readyCheckAlertKey(state) : null
    if (eventKey) cancelCriticalPlayerSound('ready_check', eventKey)
    playReadyCheckDeclineSound()
    await assertResult(playerApi.declineReadyCheck())
    clearReadyCheckHold()
    await loadPlayerMatchmakerState()
  } catch (error) {
    if (error instanceof PlayerSessionUnauthorizedError) {
      handleUnauthorizedPlayerSession()
      return
    }
    if (error instanceof PlayerAccessRequiredError) {
      handlePlayerAccessRequired()
      return
    }
    matchmakerError.value = error instanceof Error ? error.message : String(error)
  } finally {
    matchmakerActionPending.value = false
    matchmakerLoading.value = false
  }
}

function startReadyCheckAcceptedHold(): void {
  const now = Date.now()
  const totalCount = readyCheckTotalCount.value
  const acceptedCount = Math.min(totalCount, readyCheckAcceptedCount.value + (isReadyCheckAccepted.value ? 0 : 1))
  readyCheckHoldUntilMs.value = now + readyCheckAcceptedHoldMs
  readyCheckHoldSnapshot.value = {
    modeLabel: readyCheckModeLabel.value,
    acceptedCount,
    totalCount,
    slots: buildReadyCheckDisplaySlots(
      totalCount,
      matchmakerState.value?.ready_check?.players ?? [],
      acceptedCount,
      matchmakerState.value?.handle,
    ),
  }
  nowMs.value = now
}

function syncReadyCheckHoldWithState(): void {
  if (!readyCheckHoldSnapshot.value) return

  if (playerQueueStatus.value === 'ready_check') {
    readyCheckHoldSnapshot.value = {
      modeLabel: readyCheckModeLabel.value,
      acceptedCount: readyCheckAcceptedCount.value,
      totalCount: readyCheckTotalCount.value,
      slots: buildReadyCheckDisplaySlots(
        readyCheckTotalCount.value,
        matchmakerState.value?.ready_check?.players ?? [],
        readyCheckAcceptedCount.value,
      ),
    }
    return
  }

  if (playerQueueStatus.value === 'in_match') {
    readyCheckHoldSnapshot.value = {
      ...readyCheckHoldSnapshot.value,
      acceptedCount: readyCheckHoldSnapshot.value.totalCount,
      slots: readyCheckHoldSnapshot.value.slots.map(slot => ({ ...slot, accepted: true })),
    }
    return
  }

  clearReadyCheckHold()
}

function clearReadyCheckHold(): void {
  readyCheckHoldUntilMs.value = null
  readyCheckHoldSnapshot.value = null
}

function buildReadyCheckDisplaySlots(
  totalCount: number,
  players: ReadyCheckPlayer[],
  acceptedCount: number,
  forceAcceptedHandle = '',
): ReadyCheckDisplaySlot[] {
  const total = Math.max(1, totalCount)
  const slots = players.slice(0, total).map((player, index) => ({
    index,
    accepted: Boolean(player.accepted || (forceAcceptedHandle && player.handle === forceAcceptedHandle)),
    avatarUrl: player.avatar_url || '',
    label: player.display_name || player.steam_id || '',
  }))
  for (let index = slots.length; index < total; index += 1) {
    slots.push({
      index,
      accepted: index < acceptedCount,
      avatarUrl: '',
      label: '',
    })
  }
  return slots
}

async function assertResult(resultPromise: ReturnType<typeof playerApi.joinQueue>): Promise<void> {
  const result = await resultPromise
  if (!result.ok) {
    if (result.code === 'queue_full') {
      throw new Error('Для группы такого размера формат отключён или достигнут лимит ожидающих групп.')
    }
    throw new Error(result.error || 'request failed')
  }
}
</script>

<template>
  <div class="dota-shell">
    <DashboardBackdrop v-if="backdropEnabled" :section="topbarState" />
    <header
      ref="navRoot"
      class="dnav"
      :class="{ 'mobile-menu-open': mobileMenuOpen }"
      :inert="showReadyCheckOverlay || playerDockModalOpen"
    >
      <div class="dnav-bg dnav-bg-base"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-home" :class="{ visible: topbarState === 'home' }"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-matches" :class="{ visible: topbarState === 'matches' }"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-players" :class="{ visible: topbarState === 'players' }"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-watch" :class="{ visible: topbarState === 'watch' }"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-meta" :class="{ visible: topbarState === 'meta' }"></div>
      <div class="dnav-bg dnav-bg-active dnav-bg-start" :class="{ visible: topbarState === 'start' }"></div>
      <div class="dnav-content">
        <div class="dnav-auth">
          <div ref="settingsRoot" class="dnav-settings">
            <button
              class="settings-button"
              type="button"
              aria-label="Settings"
              :aria-expanded="settingsMenuOpen"
              @click="toggleSettingsMenu"
            ></button>
            <div
              v-if="settingsMenuOpen"
              class="settings-menu"
              role="dialog"
              aria-label="Настройки"
            >
              <PlayerSoundSettings
                :authenticated="isAuthenticated"
                :push-state="playerWebPushState"
                @enable-push="enablePlayerWebPush"
                @disable-push="disablePlayerWebPush"
              />
              <button
                v-if="isAuthenticated"
                class="settings-menu-action"
                type="button"
                :disabled="sessionLoading"
                @click="logout"
              >
                Выйти
              </button>
              <button
                v-else
                class="settings-menu-action primary"
                type="button"
                :disabled="sessionLoading"
                @click="loginSteam"
              >
                {{ sessionExpired ? 'Войти снова' : 'Войти через Steam' }}
              </button>
            </div>
          </div>
          <div
            v-if="isAuthenticated"
            id="player-notification-host"
            class="player-notification-host"
          ></div>
          <button
            v-else
            class="profile-chip login"
            :class="{ expired: sessionExpired }"
            type="button"
            :disabled="sessionLoading"
            @click="loginSteam"
          >
            <span class="steam-login-icon" aria-hidden="true"></span>
            <span>SIGN IN</span>
          </button>
        </div>

        <RouterLink
          to="/"
          class="dnav-brand-hit dnav-brand-link"
          aria-label="Home"
        />

        <button
          class="dnav-mobile-toggle"
          type="button"
          :aria-label="mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'"
          aria-controls="mobile-primary-menu"
          :aria-expanded="mobileMenuOpen"
          @click="toggleMobileMenu"
        ></button>

        <nav class="dnav-nav" aria-label="Primary">
          <RouterLink
            to="/matches"
            class="dnav-item"
            :class="{ active: matchesNavActive }"
            :aria-current="matchesNavActive ? 'page' : undefined"
          >
            <span class="dnav-label">Матчи</span>
          </RouterLink>
          <RouterLink
            to="/players"
            class="dnav-item"
            :class="{ active: playersNavActive }"
            :aria-current="playersNavActive ? 'page' : undefined"
          >
            <span class="dnav-label">Игроки</span>
          </RouterLink>
          <RouterLink
            to="/stats/heroes"
            class="dnav-item"
            :class="{ active: metaNavActive }"
            :aria-current="metaNavActive ? 'page' : undefined"
          >
            <span class="dnav-label">Мета</span>
          </RouterLink>
          <RouterLink
            to="/live"
            class="dnav-item dnav-item-watch"
            :class="{ active: watchNavActive }"
            :aria-current="watchNavActive ? 'page' : undefined"
            :aria-label="liveNavAriaLabel"
          >
            <span class="dnav-label">Смотреть</span>
            <span
              v-if="liveBroadcastCount > 0"
              class="dnav-live-count"
              :class="{ capped: liveBroadcastCount > 99 }"
              aria-hidden="true"
            >{{ liveBroadcastCountLabel }}</span>
          </RouterLink>
          <RouterLink
            to="/start"
            class="dnav-item"
            :class="{ active: startNavActive }"
            :aria-current="startNavActive ? 'page' : undefined"
          >
            <span class="dnav-label">Как начать</span>
          </RouterLink>
        </nav>

        <nav
          id="mobile-primary-menu"
          class="dnav-mobile-menu"
          :class="{ open: mobileMenuOpen }"
          aria-label="Mobile primary"
        >
          <RouterLink
            to="/"
            class="dnav-mobile-item"
            :class="{ active: topbarState === 'home' }"
            :aria-current="topbarState === 'home' ? 'page' : undefined"
            @click="closeMobileMenu"
          >
            Главная
          </RouterLink>
          <RouterLink
            to="/matches"
            class="dnav-mobile-item"
            :class="{ active: matchesNavActive }"
            :aria-current="matchesNavActive ? 'page' : undefined"
            @click="closeMobileMenu"
          >
            Матчи
          </RouterLink>
          <RouterLink
            to="/players"
            class="dnav-mobile-item"
            :class="{ active: playersNavActive }"
            :aria-current="playersNavActive ? 'page' : undefined"
            @click="closeMobileMenu"
          >
            Игроки
          </RouterLink>
          <RouterLink
            to="/stats/heroes"
            class="dnav-mobile-item"
            :class="{ active: metaNavActive }"
            :aria-current="metaNavActive ? 'page' : undefined"
            @click="closeMobileMenu"
          >
            Мета
          </RouterLink>
          <RouterLink
            to="/live"
            class="dnav-mobile-item"
            :class="{ active: watchNavActive }"
            :aria-current="watchNavActive ? 'page' : undefined"
            :aria-label="liveNavAriaLabel"
            @click="closeMobileMenu"
          >
            <span>Смотреть</span>
            <span
              v-if="liveBroadcastCount > 0"
              class="dnav-live-count"
              :class="{ capped: liveBroadcastCount > 99 }"
              aria-hidden="true"
            >{{ liveBroadcastCountLabel }}</span>
          </RouterLink>
          <RouterLink
            to="/start"
            class="dnav-mobile-item"
            :class="{ active: startNavActive }"
            :aria-current="startNavActive ? 'page' : undefined"
            @click="closeMobileMenu"
          >
            Как начать
          </RouterLink>
        </nav>
      </div>
    </header>

    <div
      ref="pageScroll"
      class="page-scroll"
      :class="{ 'player-social-hidden': !isAuthenticated }"
      :inert="showReadyCheckOverlay || playerDockModalOpen"
    >
      <main class="site-main">
        <RouterView />
      </main>
    </div>

    <PlayerDock
      ref="playerProfileSocialActions"
      :authenticated="isAuthenticated"
      :access-allowed="playerAccessAllowed"
      :verification-contact-url="verificationContactURL"
      :player="currentPlayer"
      :chat-capabilities="playerChatCapabilities"
      :communication-restrictions="playerCommunicationRestrictions"
      :hidden="showReadyCheckOverlay"
      :external-surface-open="searchPanelOpen"
      :navigation-surface-open="mobileMenuOpen || settingsMenuOpen"
      :matchmaker-status="playerQueueStatus"
      :party-roster-locked="partyRosterLock.locked"
      :party-roster-lock-reason="partyRosterLock.reason"
      :matchmaker-busy="sessionLoading || matchmakerActionPending"
      :inert="showReadyCheckOverlay"
      @unauthorized="handleUnauthorizedPlayerSession"
      @access-required="handlePlayerAccessRequired"
      @party-changed="handlePlayerPartyChanged"
      @communication-policy-changed="communicationPolicyLiveRefreshQueue.request"
      @party-state="handlePlayerPartyState"
      @party-busy="handlePlayerPartyBusy"
      @dock-modal="handlePlayerDockModal"
      @close-external-surface="closePlayerExternalSurfaces"
    >
      <template #play>
        <div v-if="!isSearching" class="match-cta-stack">
          <div
            v-if="canLeaveCurrentMatch"
            class="match-leave-notice"
            :class="matchLeaveNoticeTone"
            role="status"
          >
            <strong>{{ matchLeaveNoticeTitle }}</strong>
            <span>{{ matchLeaveNoticeScore }}</span>
          </div>
          <button
            v-if="canLeaveCurrentMatch"
            class="match-leave"
            type="button"
            aria-label="Ливнуть из матча"
            :disabled="actionBusy"
            @click="leaveCurrentMatch"
          >
            <span>Leave</span>
          </button>
          <CopyCommandButton
            v-if="connectCommand"
            class="match-connect"
            :command="connectCommand"
            :idle-label="matchConnectBaseLabel"
            :accessible-label="`Copy command for Dota 2: ${connectCommand}`"
            copied-label="Copied"
            failed-label="Copy failed"
            :reset-after-ms="1600"
          />
          <button
            v-if="showReadyCheckCompact"
            class="ready-check-compact"
            type="button"
            aria-label="Развернуть ожидание игроков"
            @click="expandReadyCheck"
          >
            <span class="ready-check-compact-label">Игра принята</span>
            <strong>{{ visibleReadyCheckModeLabel }}</strong>
            <span class="ready-check-compact-progress">Готово: {{ visibleReadyCheckAcceptedCount }}/{{ visibleReadyCheckTotalCount }}</span>
          </button>
          <button
            v-if="showPrimaryAction"
            ref="findMatchButton"
            class="find-match"
            :class="{
              confirm: searchPanelOpen && !selectedQueueBan && !selectedQueuePartySizeBlocked,
              banned: searchPanelOpen && selectedQueueBan,
              offline: playerServiceUnavailable,
              'approval-required': playerAccessRestricted && playerQueueStatus === 'idle',
            }"
            type="button"
            :aria-label="actionLabel"
            :disabled="actionDisabled"
            @click="handleFindMatch"
          >
            <span>{{ actionLabel }}</span>
          </button>
        </div>
        <div v-else class="search-status-panel" role="group" aria-label="Match search">
          <div class="search-status-meta">
            <span>{{ currentQueueTitle }}</span>
            <span>{{ currentModeLabel }}</span>
            <time aria-hidden="true">{{ searchElapsedLabel }}</time>
          </div>
          <div class="search-status-main">
            <span class="search-status-title">
              Поиск матча
              <span v-if="currentQueueProgressLabel" class="search-status-count">{{ currentQueueProgressLabel }}</span>
            </span>
            <button
              ref="searchCancelButton"
              type="button"
              :disabled="actionBusy || !currentPartyCanCancelMatchmaking"
              :aria-label="searchCancelLabel"
              :title="searchCancelLabel"
              @click="runMatchmakerAction"
            >
              <span class="player-chat-visually-hidden">{{ searchCancelLabel }}</span>
            </button>
          </div>
        </div>
        <PlayerQueueLanes
          v-if="showQueueLanes"
          :background="queueLaneBackground"
          :offers="queueLanes.offers"
          :busy="actionBusy"
          :can-cancel="currentPartyCanCancelMatchmaking"
          @join="queueID => runQueueLaneAction(queueID, true)"
          @leave="queueID => runQueueLaneAction(queueID, false)"
        />
      </template>
      <template #external-surface>
        <MatchSearchPanel
          :open="searchPanelOpen && !showReadyCheckOverlay"
          :queues="matchmakerState?.queues ?? []"
          :selected-queue-id="currentQueueID"
          :party-size="currentPartySize"
          :error="matchmakerError"
          :selected-queue-notice="selectedQueueNotice"
          @close="closeSearchPanel"
          @select-queue="selectQueue"
        />
      </template>
    </PlayerDock>
    <PlayerApprovalNotice
      v-if="showApprovalWelcome && !showReadyCheckOverlay"
      :player-name="currentPlayer?.persona_name"
      :discord-url="discordInviteURL"
      @close="closeApprovalWelcome"
    />
    <Transition
      name="ready-check-overlay"
      @before-enter="activateReadyCheckDialog"
      @before-leave="deactivateReadyCheckDialog"
    >
      <div
        v-if="showReadyCheckOverlay"
        ref="readyCheckDialog"
        class="ready-check-stage"
        :class="{ accepted: readyCheckDisplayAccepted }"
        role="dialog"
        aria-modal="true"
        aria-label="Ready check"
        tabindex="-1"
        @keydown="trapReadyCheckFocus"
      >
        <div class="ready-check-cube">
          <section
            class="ready-check-face ready-check-face-front"
            aria-label="Match ready"
            :aria-hidden="readyCheckDisplayAccepted"
            :inert="readyCheckDisplayAccepted"
          >
            <div class="ready-check-copy">
              <span>Ваша игра готова</span>
              <strong>{{ visibleReadyCheckModeLabel }}</strong>
            </div>
            <div class="ready-check-actions">
              <button
                ref="readyCheckAccept"
                class="ready-check-accept"
                type="button"
                :disabled="actionBusy || !playerAccessAllowed"
                @click="runMatchmakerAction"
              >
                Принять
              </button>
              <button class="ready-check-decline" type="button" :disabled="actionBusy" @click="declineReadyCheck">
                Отклонить
              </button>
            </div>
          </section>
          <section
            class="ready-check-face ready-check-face-waiting"
            aria-label="Waiting for players"
            :aria-hidden="!readyCheckDisplayAccepted"
            :inert="!readyCheckDisplayAccepted"
          >
            <button
              v-if="canCollapseReadyCheck"
              ref="readyCheckCollapse"
              class="ready-check-collapse"
              type="button"
              aria-label="Свернуть ожидание игроков"
              @click="collapseReadyCheck"
            ></button>
            <div class="ready-check-copy">
              <span>Ожидание игроков</span>
              <strong>{{ visibleReadyCheckModeLabel }}</strong>
            </div>
            <div class="ready-check-slots" :aria-label="`Готово: ${visibleReadyCheckAcceptedCount}/${visibleReadyCheckTotalCount}`">
              <span
                v-for="slot in visibleReadyCheckSlots"
                :key="slot.index"
                class="ready-check-slot"
                :class="{ accepted: slot.accepted }"
                :title="slot.label"
              >
                <img
                  v-if="slot.avatarUrl"
                  class="ready-check-slot-avatar"
                  :src="slot.avatarUrl"
                  :alt="slot.label || 'Player'"
                />
                <span v-else class="ready-check-slot-icon"></span>
              </span>
            </div>
            <div class="ready-check-progress">Готово: {{ visibleReadyCheckAcceptedCount }}/{{ visibleReadyCheckTotalCount }}</div>
          </section>
        </div>
      </div>
    </Transition>
  </div>
</template>
